import { useQuery } from '@tanstack/react-query';
import gql from 'graphql-tag';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useMemberGlobalSettingsUpdate from 'hooks/models/member/useMemberGlobalSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { cloneDeep, compact, flatMap, includes, isEmpty, isNil, pickBy, uniq } from 'lodash';
import keyBy from 'lodash/keyBy';
import { Nullable } from 'types/util/Nullable';
import { t } from 'utils/localize';
import { typedObjectKeys } from 'utils/objectUtils';
import { ITimeEntryTemplate } from '../types/types';

const EMPTY_TEMPLATES: Record<string, ITimeEntryTemplate> = {};

// Comfortably above the number of distinct components any one member's set of templates could realistically reference.
const MAX_ARCHIVED_COMPONENT_CHECK_COUNT = 500;

interface IArchivableOrDeletableEntity {
  id: string;
  archivedOn?: Nullable<string>;
  deletedOn?: Nullable<string>;
}

interface IQuickTimeTemplateArchivedComponentsQueryResult {
  projects?: IArchivableOrDeletableEntity[];
  costCodes?: IArchivableOrDeletableEntity[];
  equipment?: IArchivableOrDeletableEntity[];
  members?: IArchivableOrDeletableEntity[];
}

interface IQuickTimeTemplateArchivedComponentsQueryVariables {
  [key: string]: unknown;
  checkProjects: boolean;
  projectFilter: { id: { contains: string[] } };
  checkCostCodes: boolean;
  costCodeFilter: { id: { contains: string[] } };
  checkEquipment: boolean;
  equipmentFilter: { id: { contains: string[] } };
  checkMembers: boolean;
  memberFilter: { id: { contains: string[] } };
}

export const QUICK_TIME_TEMPLATE_ARCHIVED_COMPONENTS_QUERY = gql`
  query queryQuickTimeTemplateArchivedComponents(
    $checkProjects: Boolean!
    $projectFilter: ProjectFilter
    $checkCostCodes: Boolean!
    $costCodeFilter: CostCodeFilter
    $checkEquipment: Boolean!
    $equipmentFilter: EquipmentFilter
    $checkMembers: Boolean!
    $memberFilter: MemberFilter
  ) {
    projects(first: ${MAX_ARCHIVED_COMPONENT_CHECK_COUNT}, filter: $projectFilter) @include(if: $checkProjects) {
      id
      archivedOn
    }
    costCodes(first: ${MAX_ARCHIVED_COMPONENT_CHECK_COUNT}, filter: $costCodeFilter) @include(if: $checkCostCodes) {
      id
      archivedOn
    }
    equipment(first: ${MAX_ARCHIVED_COMPONENT_CHECK_COUNT}, filter: $equipmentFilter) @include(if: $checkEquipment) {
      id
      archivedOn
      deletedOn
    }
    members(first: ${MAX_ARCHIVED_COMPONENT_CHECK_COUNT}, filter: $memberFilter) @include(if: $checkMembers) {
      id
      archivedOn
      deletedOn
    }
  }
`;

function toArchivedOrDeletedIds(items: IArchivableOrDeletableEntity[] | undefined) {
  return (items ?? []).filter((item) => !isNil(item.archivedOn) || !isNil(item.deletedOn)).map((item) => item.id);
}

export type ArchivedTemplateComponentType = 'project' | 'costCode' | 'equipment' | 'employee';

export interface IArchivedTemplateComponent {
  type: ArchivedTemplateComponentType;
  id: string;
}

interface IArchivedComponentIds {
  archivedProjectIds: string[];
  archivedCostCodeIds: string[];
  archivedEquipmentIds: string[];
  archivedMemberIds: string[];
}

function getArchivedTemplateComponents(
  template: ITimeEntryTemplate,
  { archivedProjectIds, archivedCostCodeIds, archivedEquipmentIds, archivedMemberIds }: IArchivedComponentIds
): IArchivedTemplateComponent[] {
  const components: IArchivedTemplateComponent[] = [];

  if (template.projectId && includes(archivedProjectIds, template.projectId)) {
    components.push({ type: 'project', id: template.projectId });
  }
  if (template.costCodeId && includes(archivedCostCodeIds, template.costCodeId)) {
    components.push({ type: 'costCode', id: template.costCodeId });
  }
  if (template.equipmentId && includes(archivedEquipmentIds, template.equipmentId)) {
    components.push({ type: 'equipment', id: template.equipmentId });
  }
  (template.members ?? []).forEach((memberId) => {
    if (includes(archivedMemberIds, memberId)) {
      components.push({ type: 'employee', id: memberId });
    }
  });

  return components;
}

const ARCHIVED_TEMPLATE_COMPONENT_MESSAGES: Record<ArchivedTemplateComponentType, () => string> = {
  project: () =>
    t(
      'The project linked to this template is currently archived. To use this template, please unarchive the project or select a different active project.'
    ),
  costCode: () =>
    t(
      'The cost code linked to this template is currently archived. To use this template, please unarchive the cost code or select a different active cost code.'
    ),
  equipment: () =>
    t(
      'The equipment linked to this template is currently archived. To use this template, please select a different equipment.'
    ),
  employee: () =>
    t(
      'The employee linked to this template is currently archived or deleted. To use this template, please unarchive the employee or select a different active employee.'
    ),
};

export function getArchivedTemplateComponentsTooltip(components: IArchivedTemplateComponent[]): string | null {
  if (components.length === 0) {
    return null;
  }
  if (components.length === 1) {
    return ARCHIVED_TEMPLATE_COMPONENT_MESSAGES[components[0].type]();
  }
  return t(
    'Items linked to this template are currently archived/deleted. To use this template, please unarchive the items or select different active items.'
  );
}

function useArchivedComponentIds(templateValues: ITimeEntryTemplate[], enabled: boolean) {
  const projectIds = uniq(compact(templateValues.map((template) => template.projectId)));
  const costCodeIds = uniq(compact(templateValues.map((template) => template.costCodeId)));
  const equipmentIds = uniq(compact(templateValues.map((template) => template.equipmentId)));
  const memberIds = uniq(compact(flatMap(templateValues, (template) => template.members ?? [])));

  const hasArchivableComponentsToCheck =
    !isEmpty(projectIds) || !isEmpty(costCodeIds) || !isEmpty(equipmentIds) || !isEmpty(memberIds);

  const graphqlClient = useGraphQLClient();
  const baseQueryKey = useReactQueryBaseKey();

  const archivedComponentsQuery = useQuery({
    queryKey: [
      ...baseQueryKey,
      'quickTimeTemplateArchivedComponents',
      projectIds,
      costCodeIds,
      equipmentIds,
      memberIds,
    ],
    queryFn: () =>
      graphqlClient.request<
        IQuickTimeTemplateArchivedComponentsQueryResult,
        IQuickTimeTemplateArchivedComponentsQueryVariables
      >(QUICK_TIME_TEMPLATE_ARCHIVED_COMPONENTS_QUERY, {
        checkProjects: !isEmpty(projectIds),
        projectFilter: { id: { contains: projectIds } },
        checkCostCodes: !isEmpty(costCodeIds),
        costCodeFilter: { id: { contains: costCodeIds } },
        checkEquipment: !isEmpty(equipmentIds),
        equipmentFilter: { id: { contains: equipmentIds } },
        checkMembers: !isEmpty(memberIds),
        memberFilter: { id: { contains: memberIds } },
      }),
    enabled: enabled && hasArchivableComponentsToCheck,
  });

  const isLoading = enabled && hasArchivableComponentsToCheck && archivedComponentsQuery.isLoading;

  return {
    isLoading,
    archivedProjectIds: toArchivedOrDeletedIds(archivedComponentsQuery.data?.projects),
    archivedCostCodeIds: toArchivedOrDeletedIds(archivedComponentsQuery.data?.costCodes),
    archivedEquipmentIds: toArchivedOrDeletedIds(archivedComponentsQuery.data?.equipment),
    archivedMemberIds: toArchivedOrDeletedIds(archivedComponentsQuery.data?.members),
  };
}

// Always checks for archived/deleted components (regardless of includeArchivedComponents) so that UI showing every
// template (e.g. the template management list) can flag which ones reference an archived or deleted component.
export function useQuickTimeTemplateArchivedComponents(templates: Record<string, ITimeEntryTemplate>) {
  const templateValues = Object.values(templates);
  const { isLoading, ...archivedIds } = useArchivedComponentIds(templateValues, true);

  const archivedComponentsByTemplateName: Record<string, IArchivedTemplateComponent[]> = {};
  if (!isLoading) {
    templateValues.forEach((template) => {
      const components = getArchivedTemplateComponents(template, archivedIds);
      if (components.length > 0) {
        archivedComponentsByTemplateName[template.name] = components;
      }
    });
  }

  return { isLoading, archivedComponentsByTemplateName };
}

type DeMetaQuickTimeTemplate = Omit<ITimeEntryTemplate, 'position' | 'name'>;

export interface IQuickTimeTemplateKeyAdapter<T> {
  projectKey: keyof T;
  costCodeKey: keyof T;
  equipmentKey: keyof T;
  startTimeKey: keyof T;
  endTimeKey: keyof T;
  membersKey: keyof T;
  descriptionKey: keyof T;
  breakKey: keyof T;
}

export type QuickTimeTemplateAdapter<T> = Record<keyof DeMetaQuickTimeTemplate, keyof T>;

export function createQuickTimeTemplateAdapter<T>(
  adapter: IQuickTimeTemplateKeyAdapter<T>
): QuickTimeTemplateAdapter<T> {
  return {
    projectId: adapter.projectKey,
    costCodeId: adapter.costCodeKey,
    equipmentId: adapter.equipmentKey,
    members: adapter.membersKey,
    startTime: adapter.startTimeKey,
    endTime: adapter.endTimeKey,
    description: adapter.descriptionKey,
    breaks: adapter.breakKey,
  };
}

export function convertQuickTimeTemplate<T>(
  template: DeMetaQuickTimeTemplate,
  adapter: QuickTimeTemplateAdapter<T>
): T {
  return typedObjectKeys(template).reduce(
    (acc, cur) => ({
      ...acc,
      [adapter[cur]]: template[cur],
    }),
    {} as Partial<T>
  ) as T;
}

export default function useQuickTimeTemplates(includeArchivedComponents?: Nullable<boolean>) {
  const memberSettings = useMemberSettings();
  const templateSetting = memberSettings?.global?.timeEntryTemplate;
  const templates = templateSetting?.values ?? EMPTY_TEMPLATES;
  const updateGlobalMemberSettings = useMemberGlobalSettingsUpdate();

  const shouldExcludeArchivedComponents = !includeArchivedComponents;

  const templateValues = Object.values(templates);
  const { isLoading, ...archivedIds } = useArchivedComponentIds(templateValues, shouldExcludeArchivedComponents);

  const visibleTemplates = (() => {
    if (!shouldExcludeArchivedComponents) {
      return templates;
    }
    if (isLoading) {
      return EMPTY_TEMPLATES;
    }

    return pickBy(templates, (template) => getArchivedTemplateComponents(template, archivedIds).length === 0);
  })();

  const upsert = (template: ITimeEntryTemplate) => {
    return updateGlobalMemberSettings('timeEntryTemplate', {
      default: templateSetting?.default ?? null,
      values: {
        ...memberSettings?.global?.timeEntryTemplate?.values,
        [template.name]: template,
      },
    });
  };

  const edit = async (name: string, template: Partial<ITimeEntryTemplate> & { name: string }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [name]: previous, ...others } = templates;
    const newName = template.name;
    const current = templates[name];
    const isDefault = name === templateSetting?.default;

    await updateGlobalMemberSettings('timeEntryTemplate', {
      default: isDefault ? newName : (templateSetting?.default ?? null),
      values: {
        ...others,
        [newName]: {
          ...current,
          ...template,
        },
      },
    });
  };

  const set = (templates: ITimeEntryTemplate[]) => {
    const keyed = keyBy(templates, 'name');
    return updateGlobalMemberSettings('timeEntryTemplate', {
      default: templateSetting?.default ?? null,
      values: keyed,
    });
  };

  const remove = (template: ITimeEntryTemplate['name']) => {
    const cloned = cloneDeep(memberSettings?.global?.timeEntryTemplate);

    if (cloned && cloned.values[template]) {
      const isDefault = templateSetting?.default === template;
      delete cloned.values[template];
      return updateGlobalMemberSettings('timeEntryTemplate', { ...cloned, default: isDefault ? null : cloned.default });
    }
  };

  const setDefault = (name: Nullable<string>) => {
    return updateGlobalMemberSettings('timeEntryTemplate', {
      default: name,
      values: templates,
    });
  };

  return {
    isLoading,
    defaultTemplate: templateSetting?.default ? (visibleTemplates[templateSetting.default] ?? null) : null,
    templates: visibleTemplates,
    upsert,
    set,
    remove,
    edit,
    setDefault,
  };
}
